import { Cpu, GitBranch, LayoutDashboard, MessageSquare, Server } from "lucide-react";
import { LucideIcon } from "lucide-react";

export interface NavItem {
  label: string;
  href: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

export interface ProductNav {
  product: string;
  label: string;
  icon: LucideIcon;
  color: string;
  basePath: string;
  sections: NavSection[];
}

export const navigation: ProductNav[] = [
  {
    product: "flows",
    label: "Manage agents (Flows)",
    icon: GitBranch,
    color: "text-primary",
    basePath: "/docs/flows",
    sections: [
      {
        title: "Overview",
        items: [
          { label: "Introduction", href: "/docs/flows" },
          { label: "Quickstart", href: "/docs/flows/quickstart" },
          { label: "Key concepts", href: "/docs/flows/concepts" },
        ],
      },
      {
        title: "Build",
        items: [
          { label: "Your agents", href: "/docs/flows/agents" },
          { label: "The agent graph", href: "/docs/flows/graph" },
          { label: "Agent settings", href: "/docs/flows/agent-settings" },
          { label: "Sub-agents", href: "/docs/flows/sub-agents" },
          { label: "Connections", href: "/docs/flows/connections" },
        ],
      },
      {
        title: "Extend",
        items: [
          { label: "Skills", href: "/docs/flows/skills" },
          { label: "Tools", href: "/docs/flows/tools" },
          { label: "MCP servers", href: "/docs/flows/mcp-servers" },
        ],
      },
      {
        title: "Run",
        items: [
          { label: "Playground", href: "/docs/flows/playground" },
          { label: "Schedules", href: "/docs/flows/schedules" },
          { label: "Messaging", href: "/docs/flows/messaging" },
          { label: "Call the API", href: "/docs/flows/call-api" },
          { label: "A2A", href: "/docs/flows/a2a" },
        ],
      },
      {
        title: "Operate",
        items: [
          { label: "Versions", href: "/docs/flows/versions" },
          { label: "Executions", href: "/docs/flows/executions" },
          { label: "Usage analytics", href: "/docs/flows/usage" },
        ],
      },
    ],
  },
  {
    product: "admin",
    label: "Admin dashboard",
    icon: LayoutDashboard,
    color: "text-primary",
    basePath: "/docs/admin",
    sections: [
      {
        title: "Overview",
        items: [
          { label: "Introduction", href: "/docs/admin" },
          { label: "Dashboard", href: "/docs/admin/dashboard" },
        ],
      },
      {
        title: "Agent Builder",
        items: [{ label: "Overview", href: "/docs/admin/agent-builder" }],
      },
      {
        title: "Integrations",
        items: [
          { label: "Introduction", href: "/docs/admin/integrations" },
          { label: "Connecting services", href: "/docs/admin/connecting-services" },
          { label: "OAuth connections", href: "/docs/admin/oauth" },
        ],
      },
      {
        title: "Knowledge Base",
        items: [
          { label: "Overview", href: "/docs/admin/knowledge-base" },
          { label: "Managing files", href: "/docs/admin/managing-files" },
        ],
      },
      {
        title: "Organization",
        items: [
          { label: "Team", href: "/docs/admin/team" },
          { label: "Settings", href: "/docs/admin/settings" },
        ],
      },
    ],
  },
  {
    product: "chat",
    label: "Chat",
    icon: MessageSquare,
    color: "text-primary",
    basePath: "/docs/chat",
    sections: [
      {
        title: "Overview",
        items: [
          { label: "Introduction", href: "/docs/chat" },
          { label: "Quickstart", href: "/docs/chat/quickstart" },
          { label: "Key concepts", href: "/docs/chat/concepts" },
        ],
      },
      {
        title: "Talk to your agents",
        items: [
          { label: "Selecting an agent", href: "/docs/chat/selecting-agent" },
          { label: "Chat sessions", href: "/docs/chat/sessions" },
        ],
      },
      {
        title: "Meta-Agent",
        items: [
          { label: "Meet the Meta-Agent", href: "/docs/chat/meta-agent" },
          { label: "Build an agent", href: "/docs/chat/build-agent" },
          { label: "Refine an agent", href: "/docs/chat/refine-agent" },
        ],
      },
      {
        title: "Features",
        items: [
          { label: "Slash commands", href: "/docs/chat/slash-commands" },
          { label: "File uploads", href: "/docs/chat/file-uploads" },
        ],
      },
    ],
  },
  {
    product: "mcp-server",
    label: "Anter MCP Server",
    icon: Server,
    color: "text-primary",
    basePath: "/docs/mcp-server",
    sections: [
      {
        title: "Overview",
        items: [
          { label: "Introduction", href: "/docs/mcp-server" },
          { label: "Quickstart", href: "/docs/mcp-server/quickstart" },
          { label: "Key concepts", href: "/docs/mcp-server/concepts" },
        ],
      },
      {
        title: "Tools",
        items: [
          { label: "Agent lifecycle", href: "/docs/mcp-server/agent-lifecycle" },
          { label: "Running & observability", href: "/docs/mcp-server/execution" },
          { label: "Projects & LLM providers", href: "/docs/mcp-server/providers" },
          { label: "MCP server management", href: "/docs/mcp-server/servers" },
          { label: "Skills & toolsets", href: "/docs/mcp-server/skills-tools" },
          { label: "Utility tools", href: "/docs/mcp-server/utility" },
        ],
      },
    ],
  },
  {
    product: "runtime",
    label: "Agent Runtime",
    icon: Cpu,
    color: "text-primary",
    basePath: "/docs/runtime",
    sections: [
      {
        title: "Overview",
        items: [
          { label: "Introduction", href: "/docs/runtime" },
          { label: "How a run executes", href: "/docs/runtime/execution-model" },
        ],
      },
      {
        title: "Orchestration",
        items: [
          { label: "Transfer vs. delegate", href: "/docs/runtime/transfer-vs-delegate" },
          { label: "Designing a routing graph", href: "/docs/runtime/routing-graph-design" },
          { label: "Correction nudges", href: "/docs/runtime/correction-nudges" },
        ],
      },
      {
        title: "Limits & Reliability",
        items: [
          { label: "Limits and budgets", href: "/docs/runtime/limits-and-budgets" },
          { label: "Reading the routing timeline", href: "/docs/runtime/routing-timeline" },
        ],
      },
      {
        title: "Trust & Continuity",
        items: [
          { label: "Permissions and approvals", href: "/docs/runtime/permissions" },
          { label: "Memory and conversation boundaries", href: "/docs/runtime/memory" },
          { label: "Resuming and streaming", href: "/docs/runtime/resume-streaming" },
        ],
      },
      {
        title: "Cross-Agent",
        items: [{ label: "Agent-to-agent dispatch", href: "/docs/runtime/agent-to-agent" }],
      },
    ],
  },
];
